/*
 * chart-types — the chart system's shared types, config, palette, and the pure
 * scale/path math. A faithful port of the v2 chart (Charts/types.ts + the
 * load-bearing math out of UniversalChart.tsx), reading a RingBuffer directly
 * so a frame allocates no intermediate point arrays.
 *
 * The DATA palette is the ONE shared `chartUtils.CHART_THEMES` (the same palette
 * the symbol grid sparklines use) — never a second source. STRUCTURAL colors
 * (gridlines, axes, labels, cursor) are CSS tokens in the SVG markup, so they
 * track light/dark for free. No hardcoded colors live here.
 */
import {
  CHART_THEMES,
  calculatePaddingFactor,
  type ChartThemeName,
  SELECTABLE_CHART_THEMES,
} from '../../shell/symbolGrid/chartUtils';
import type { RingBuffer } from './ring-buffer';

export { CHART_THEMES, calculatePaddingFactor };
export type ThemeOption = ChartThemeName;

/** Theme names offered in the config dialog (frost default first; 'blue' hidden). */
export const THEME_OPTIONS: readonly ThemeOption[] = SELECTABLE_CHART_THEMES;

// ── config + threshold + ghost + stats shapes ───────────────────────────────

/** A horizontal line the reader placed; crossing it can freeze the chart. */
export interface ChartThreshold {
  id: string;
  value: number;
  condition: 'above' | 'below';
  pauseOnCross: boolean;
}

/** Threshold colour: purple, desaturating toward grey per index. */
export function getThresholdColor(index: number): string {
  const s = Math.max(15, 85 - index * 20);
  const l = Math.min(78, 70 + index * 3);
  return `hsl(264, ${s}%, ${l}%)`;
}

export interface ChartGhost {
  id: string;
  /** relTime = ms offset from capture end (0 = newest, negative = older). */
  data: { relTime: number; value: number }[];
  capturedAt: number;
  timeWindowMs: number;
  sampleRateHz: number;
}

/** Ghost color: orange, desaturating toward grey per index. */
export function getGhostColor(index: number): string {
  const s = Math.max(15, 92 - index * 22);
  const l = Math.min(75, 52 + index * 3);
  return `hsl(38, ${s}%, ${l}%)`;
}

export interface ChartConfig {
  timeWindowMs: number;
  sampleRateHz: number;
  theme?: ThemeOption;
  customName?: string;
  yAxisMin?: number;
  yAxisMax?: number;
  thresholds?: ChartThreshold[];
  showStats?: boolean;
  ghosts?: ChartGhost[];
}

/** Stable y-axis anchor — running min/max the owner tracks per series. */
export interface ChartStats {
  allTimeMin: number;
  allTimeMax: number;
}

/** A measure-tool cursor pair, in chart-relative X (0-1); `relXB` is null while placing. */
export interface MeasurePair {
  id: string;
  relXA: number;
  relXB: number | null;
}

export const DEFAULT_CHART_CONFIG: ChartConfig = {
  timeWindowMs: 30_000,
  sampleRateHz: 10,
  theme: 'frost',
};

export const TIME_WINDOWS: readonly { label: string; value: number }[] = [
  { label: '10s', value: 10_000 },
  { label: '30s', value: 30_000 },
  { label: '1m', value: 60_000 },
  { label: '5m', value: 5 * 60_000 },
  { label: '10m', value: 10 * 60_000 },
  { label: '30m', value: 30 * 60_000 },
];

export const SAMPLE_RATES: readonly { label: string; value: number; interval: string }[] = [
  { label: '1 Hz', value: 1, interval: '1000ms' },
  { label: '5 Hz', value: 5, interval: '200ms' },
  { label: '10 Hz', value: 10, interval: '100ms' },
  { label: '20 Hz', value: 20, interval: '50ms' },
  { label: '50 Hz', value: 50, interval: '20ms' },
];

/** How many samples a window holds at a rate — the ring size, and the figure the dialog shows. */
export function bufferSizeFor(config: ChartConfig): number {
  return Math.ceil((config.timeWindowMs / 1000) * config.sampleRateHz);
}

// ── stats ───────────────────────────────────────────────────────────────────

export interface WindowStats {
  min: number;
  max: number;
  avg: number;
  rms: number;
}

/** min/max/avg/rms over the visible window, read straight off the ring. */
export function computeWindowStats(ring: RingBuffer, timeWindowMs: number, now: number): WindowStats | null {
  const startTime = now - timeWindowMs;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let i = 0; i < ring.count; i++) {
    const idx = (ring.head + i) % ring.capacity;
    if (ring.timestamps[idx] < startTime) continue;
    const v = ring.values[idx];
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
    sumSq += v * v;
    n++;
  }
  if (n === 0) return null;
  return { min, max, avg: sum / n, rms: Math.sqrt(sumSq / n) };
}

/** Adaptive-precision stat formatter (M/K suffixes). */
export function formatStatValue(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1000) return (value / 1000).toFixed(1) + 'K';
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toFixed(4);
}

/** A stat label bounded to a few characters so a threshold tag never widens. */
export function formatStatLabel(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (abs >= 10_000) return (value / 1000).toFixed(0) + 'K';
  if (abs >= 1000) return (value / 1000).toFixed(1) + 'K';
  if (abs >= 100) return value.toFixed(1);
  if (abs >= 1) return value.toFixed(2);
  return value.toFixed(3);
}

/** A time span for the measure panel: ms under a second, s under a minute, else m:ss. */
export function formatTimeDelta(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

/** Number formatter for the row value + current-value label. */
export function formatValue(n: number): string {
  if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + 'k';
  if (Math.abs(n) >= 1) return n.toFixed(2);
  return n.toFixed(4);
}

/** The hover chip's value: M/K suffixes, three decimals from one up, four below. */
export function formatChartValue(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1000) return (v / 1000).toFixed(1) + 'K';
  if (abs >= 1) return v.toFixed(3);
  return v.toFixed(4);
}

/** The hover chip's time: wall clock to the millisecond. */
export function formatChartTime(tsMs: number): string {
  const d = new Date(tsMs);
  const p2 = (n: number): string => String(n).padStart(2, '0');
  return `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}.${String(d.getMilliseconds()).padStart(3, '0')}`;
}

// ── y-axis scale ──────────────────────────────────────────────────────────────

export interface YAxisTick {
  value: number;
  y: number;
  label: string;
}
/**
 * One more trace on the same chart. Each series keeps its own ring and draws
 * on its own scale: a load average and a percentage share one plot without
 * either flattening the other, and the legend says which value is which.
 */
export interface ChartSeries {
  ring: RingBuffer;
  /** A CSS colour or var(--token); the stroke and the legend swatch. */
  color: string;
  label: string;
  unit?: string;
}

export interface YAxisScale {
  minValue: number;
  maxValue: number;
  ticks: YAxisTick[];
}

/** A tick label with only the decimals its axis needs: an axis spanning tens reads in
 *  whole numbers, one spanning units to a tenth, a fine one to a hundredth. */
function tickLabel(value: number, range: number): string {
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  const dec = range >= 20 ? 0 : range >= 2 ? 1 : range >= 0.2 ? 2 : 3;
  return value.toFixed(dec);
}

/**
 * Y-axis min/max: a pinned bound is used as given; an unpinned one follows the
 * samples in the VISIBLE window, so the trace always fills the plot and a
 * settled value still reads at its own scale. A little headroom keeps the line
 * off the frame. Nothing to scale from gives 0..100 with no ticks.
 */
export function calculateYAxisScale(
  ring: RingBuffer,
  chartHeight: number,
  config: ChartConfig,
  now: number,
  _allTimeStats?: ChartStats | null,
): YAxisScale {
  const startTime = now - config.timeWindowMs;
  let vMin = Infinity;
  let vMax = -Infinity;
  let vCount = 0;
  for (let i = 0; i < ring.count; i++) {
    const idx = (ring.head + i) % ring.capacity;
    if (ring.timestamps[idx] < startTime) continue;
    const v = ring.values[idx];
    if (!Number.isFinite(v)) continue;
    if (v < vMin) vMin = v;
    if (v > vMax) vMax = v;
    vCount++;
  }
  const pinMin = config.yAxisMin !== undefined;
  const pinMax = config.yAxisMax !== undefined;
  if (vCount === 0 && !pinMin && !pinMax) return { minValue: 0, maxValue: 100, ticks: [] };

  // Headroom on the free side(s) only: 8% of the span, or 1 for a flat line.
  const span = vCount > 0 ? vMax - vMin : 0;
  const pad = span > 0 ? span * 0.08 : 1;
  let minVal = pinMin ? (config.yAxisMin as number) : vCount > 0 ? vMin - pad : (config.yAxisMax as number) - 100;
  let maxVal = pinMax ? (config.yAxisMax as number) : vCount > 0 ? vMax + pad : (config.yAxisMin as number) + 100;
  // A free lower bound on all-positive data stays at or above zero: a tank at 3% must not show negative litres.
  if (!pinMin && vCount > 0 && vMin >= 0 && minVal < 0) minVal = 0;
  if (!pinMax && maxVal <= minVal) maxVal = minVal + 1;

  if (minVal === maxVal) {
    minVal -= 1;
    maxVal += 1;
  }

  const yRange = maxVal - minVal;
  const ticks: YAxisTick[] = [];
  if (yRange > 0) {
    for (let i = 0; i <= 4; i++) {
      const value = minVal + (yRange * i) / 4;
      const y = chartHeight - (i * chartHeight) / 4;
      ticks.push({ value, y, label: tickLabel(value, yRange) });
    }
  } else {
    ticks.push({ value: minVal, y: chartHeight / 2, label: tickLabel(minVal, 1) });
  }
  return { minValue: minVal, maxValue: maxVal, ticks };
}

// ── x-axis ticks ──────────────────────────────────────────────────────────────

export interface XAxisTick {
  x: number;
  label: string;
}

/** Time-ago x labels (-30s … now), bucketed by window. */
export function calculateXAxisTicks(timeWindowMs: number, chartWidth: number): XAxisTick[] {
  const totalSeconds = timeWindowMs / 1000;
  if (totalSeconds <= 30) {
    return [
      { x: 0, label: `-${Math.round(totalSeconds)}s` },
      { x: chartWidth / 3, label: `-${Math.round((totalSeconds * 2) / 3)}s` },
      { x: (chartWidth * 2) / 3, label: `-${Math.round(totalSeconds / 3)}s` },
      { x: chartWidth, label: 'now' },
    ];
  }
  if (totalSeconds <= 60) {
    return [
      { x: 0, label: '-1m' },
      { x: chartWidth * 0.25, label: '-45s' },
      { x: chartWidth * 0.5, label: '-30s' },
      { x: chartWidth * 0.75, label: '-15s' },
      { x: chartWidth, label: 'now' },
    ];
  }
  const totalMinutes = totalSeconds / 60;
  const tickCount = Math.min(5, Math.floor(totalMinutes) + 1);
  const ticks: XAxisTick[] = [];
  for (let i = 0; i < tickCount; i++) {
    const minutes = Math.round(totalMinutes * (1 - i / (tickCount - 1)));
    const x = chartWidth * (i / (tickCount - 1));
    ticks.push({ x, label: minutes === 0 ? 'now' : `-${minutes}m` });
  }
  return ticks;
}

// ── line/fill path generation (per frame, from the ring) ────────────────────

export interface ChartPaths {
  linePath: string;
  fillPath: string;
  lastPointY: number | null;
}

/**
 * Build the line + gradient-fill SVG path `d` straight from the ring against
 * `now`. Samples further apart than three expected intervals are a GAP: the
 * line breaks there and each run is filled to the baseline on its own. The
 * newest run is carried to the right edge only while the series is alive
 * (its last sample under two seconds old), so a stalled source visibly stops
 * instead of flatlining forever. The ring is time-ordered, so no sort.
 */
export function generateChartPaths(
  ring: RingBuffer,
  chartWidth: number,
  chartHeight: number,
  config: ChartConfig,
  minValue: number,
  maxValue: number,
  now: number,
): ChartPaths {
  if (ring.count === 0) return { linePath: '', fillPath: '', lastPointY: null };
  const startTime = now - config.timeWindowMs;
  const lowerBound = startTime - config.timeWindowMs * calculatePaddingFactor(config.sampleRateHz);
  const range = maxValue - minValue;
  const yOf = (v: number): number => (range > 0 ? chartHeight - ((v - minValue) / range) * chartHeight : chartHeight / 2);

  /*
   * NO GAPS. The ring holds what the source PUBLISHED, and a source publishes on change: a symbol
   * that sat still for a minute produced no samples for a minute and was current the whole time.
   * Breaking the line on "missing" samples (the v2 rule, written for a resampled buffer) drew a
   * one-per-second value as a row of disconnected points. So the line runs sample to sample and
   * the newest value holds to the right edge — the reading IS the value until the next one.
   */
  let linePath = '';
  let fillPath = '';
  let prevX = 0;
  let prevY = 0;
  let started = false;
  for (let i = 0; i < ring.count; i++) {
    const idx = (ring.head + i) % ring.capacity;
    const ts = ring.timestamps[idx];
    if (ts < lowerBound) continue;
    const v = ring.values[idx];
    if (!Number.isFinite(v)) continue;
    const x = ((ts - startTime) / config.timeWindowMs) * chartWidth;
    const y = yOf(v);
    if (!started) {
      linePath = `M ${x} ${y}`;
      fillPath = `M ${x} ${chartHeight} L ${x} ${y}`;
      started = true;
    } else {
      linePath += ` L ${x} ${y}`;
      fillPath += ` L ${x} ${y}`;
    }
    prevX = x;
    prevY = y;
  }
  if (!started) return { linePath: '', fillPath: '', lastPointY: null };
  if (prevX < chartWidth) {
    linePath += ` L ${chartWidth} ${prevY}`;
    fillPath += ` L ${chartWidth} ${prevY}`;
  }
  fillPath += ` L ${chartWidth} ${chartHeight} Z`;
  return { linePath, fillPath, lastPointY: prevY };
}

/** Ghost path remapped onto the current viewport (rel-time anchored to right edge). */
export function generateGhostPath(
  ghost: ChartGhost,
  chartWidth: number,
  chartHeight: number,
  currentTimeWindowMs: number,
  minValue: number,
  maxValue: number,
): string {
  const valueRange = maxValue - minValue;
  if (ghost.data.length === 0 || valueRange <= 0) return '';
  let path = '';
  let started = false;
  for (const pt of ghost.data) {
    const xFraction = (currentTimeWindowMs + pt.relTime) / currentTimeWindowMs;
    if (xFraction < 0 || xFraction > 1) continue;
    const x = xFraction * chartWidth;
    const y = chartHeight - ((pt.value - minValue) / valueRange) * chartHeight;
    path += started ? ` L ${x} ${y}` : `M ${x} ${y}`;
    started = true;
  }
  return path;
}

// ── reading the ring at a time ───────────────────────────────────────────────

/** The sample nearest `t` (index into the ring's logical order), or -1 when empty. */
export function nearestIndex(ring: RingBuffer, t: number): number {
  if (ring.count === 0) return -1;
  const at = (i: number): number => ring.timestamps[(ring.head + i) % ring.capacity];
  let lo = 0;
  let hi = ring.count - 1;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (at(mid) < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(at(lo - 1) - t) <= Math.abs(at(lo) - t)) lo -= 1;
  return lo;
}

/**
 * The value at `t`: the last value past the newest sample, else a linear
 * interpolation between the neighbours. Null on an empty ring.
 */
export function interpolateAt(ring: RingBuffer, t: number): { value: number; timestamp: number } | null {
  if (ring.count === 0) return null;
  const at = (i: number): number => ring.timestamps[(ring.head + i) % ring.capacity];
  const vat = (i: number): number => ring.values[(ring.head + i) % ring.capacity];
  const lastIdx = ring.count - 1;
  if (t >= at(lastIdx)) return { value: vat(lastIdx), timestamp: at(lastIdx) };
  const lo = nearestIndex(ring, t);
  if (ring.count < 2) return { value: vat(lo), timestamp: at(lo) };
  let l: number;
  let r: number;
  if (at(lo) >= t) {
    r = lo;
    l = lo > 0 ? lo - 1 : 0;
  } else {
    l = lo;
    r = lo < lastIdx ? lo + 1 : lastIdx;
  }
  const lt = at(l);
  const rt = at(r);
  if (l === r || rt === lt) return { value: vat(l), timestamp: lt };
  const f = Math.max(0, Math.min(1, (t - lt) / (rt - lt)));
  return { value: vat(l) + (vat(r) - vat(l)) * f, timestamp: t };
}

/** A value snapped to a clean increment for the axis range (three significant digits of it). */
export function snapValue(value: number, minValue: number, maxValue: number): number {
  const range = maxValue - minValue;
  if (range === 0) return value;
  const magnitude = Math.pow(10, Math.floor(Math.log10(range)) - 2);
  return Math.round(value / magnitude) * magnitude;
}

/** min/max/rms of the samples between two timestamps, seeded with `seed` so an empty span reads it. */
export function rangeStats(ring: RingBuffer, tStart: number, tEnd: number, seed: number): { min: number; max: number; rms: number } {
  let min = seed;
  let max = seed;
  let sumSq = 0;
  let n = 0;
  for (let i = 0; i < ring.count; i++) {
    const idx = (ring.head + i) % ring.capacity;
    const ts = ring.timestamps[idx];
    if (ts < tStart || ts > tEnd) continue;
    const v = ring.values[idx];
    if (v < min) min = v;
    if (v > max) max = v;
    sumSq += v * v;
    n++;
  }
  return { min, max, rms: n > 0 ? Math.sqrt(sumSq / n) : 0 };
}
